import CamposDadosPaciente from 'components/paciente/CamposDadosPaciente';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { FaLightbulb } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { buscaAgendaDoPaciente } from 'services/agenda';
import { atualizarPaciente, criarPaciente, getPacientes } from 'services/pacientes';
import styles from 'styles/styles.module.css';
import { isInfrastructureError } from 'utils/apiErrors';
import { isCpfGenericoPaciente, isValidCPF } from 'utils/helpers';
import { ModalHomonimos } from './ModalHomonimos';

const CPF_CHECK_IDLE = {
  status: 'idle',
  pacientes: [],
  lastCpfChecked: '',
  infraError: null,
  message: '',
};

const PACIENTES_BLOQUEADOS = [1, 20295, 18262, 12599, 90308];

const PACIENTE_VAZIO = {
  CODPAC: '',
  CODFUNC: '',
  NOMEPAC: '',
  CPFPAC: '',
  RGPAC: '',
  DTNPAC: '',
  LOCNASCPAC: '',
  VALIDADE: '',
  PROFISSPAC: '',
  CEPPAC: '',
  ENDRESPAC: '',
  SETORPAC: '',
  OBSPAC: '',
  BAIRROPAC: '',
  CIDADEPAC: '',
  ESTADOPAC: '',
  EMPRESA: '',
  FONERESPAC: '',
  FONECOMPAC: '',
  FONERECPAC: '',
  RAMALPAC: '',
  CELULARPAC: '',
  CELULAROP: '',
  EMAILPACIENTE: '',
  ENDCOMPAC: '',
  ENDMARPAC: '',
  SEXOPAC: '',
  ESTCIVIL: '',
  CORPAC: '',
  NACIONPAC: '',
  STATUS: 'ATIVO',
  CODMEDICO: '',
  OBS: '',
  INFOPAC: '',
  dataCadastro: '',
  dataAtualizacao: '',
};

function formatIsoDateToBr(value) {
  if (!value) return '';
  const iso = String(value).substring(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }
  return String(value);
}

function toFormValues(paciente) {
  if (!paciente) return PACIENTE_VAZIO;
  return {
    ...PACIENTE_VAZIO,
    ...paciente,
    DTNPAC: paciente.DTNPAC?.substring?.(0, 10) || paciente.DTNPAC || '',
    dataCadastro: paciente.dataCadastro || formatIsoDateToBr(paciente.DATAPAC) || '',
    dataAtualizacao: paciente.dataAtualizacao || formatIsoDateToBr(paciente.DATACADPAC) || '',
  };
}

export default function ModalPaciente({
  show,
  onHide,
  mode = 'criar',
  paciente = null,
  codFunc,
  nomeFuncLogado,
  onSucesso,
}) {
  const isEditing = mode === 'editar';
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingCep, setIsFetchingCep] = useState(false);
  const [agendamentosPaciente, setAgendamentosPaciente] = useState([]);
  const [modalData, setModalData] = useState({
    visible: false,
    pacientes: [],
    dadosForm: null,
  });
  const [cpfCheck, setCpfCheck] = useState(CPF_CHECK_IDLE);
  const cpfRequestId = useRef(0);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: PACIENTE_VAZIO });

  const formSessionKey = show ? `${mode}-${paciente?.CODPAC ?? 'novo'}` : 'closed';
  const codPacExibido = paciente?.CODPAC || watch('CODPAC');

  useEffect(() => {
    async function carregarAgendas() {
      if (!paciente?.CODPAC) return;

      try {
        const data = await buscaAgendaDoPaciente(paciente.CODPAC);
        setAgendamentosPaciente(data);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Erro ao buscar agendamentos');
      }
    }

    carregarAgendas();
  }, [paciente]);

  useEffect(() => {
    if (!show) return;
    reset(isEditing ? toFormValues(paciente) : PACIENTE_VAZIO);
    setCpfCheck(CPF_CHECK_IDLE);
  }, [show, mode, paciente, isEditing, reset]);

  useEffect(() => {
    if (show && isEditing && PACIENTES_BLOQUEADOS.includes(Number(paciente?.CODPAC))) {
      toast.warn('Este paciente é protegido e não pode ser editado.');
      onHide?.();
    }
  }, [show, isEditing, paciente, onHide]);

  // Intercepta a tecla ENTER para navegar entre campos em vez de submeter
  const handleKeyDownForm = (e) => {
    // Permite Ctrl + Enter ou Cmd + Enter para salvar propositalmente
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit((dados) => handleSalvar(dados))();
      return;
    }

    if (e.key === 'Enter') {
      // Ignora se for em campo textarea (onde Enter precisa criar nova linha)
      if (e.target.tagName === 'TEXTAREA') return;

      e.preventDefault();

      // Busca todos os campos focáveis do formulário
      const form = e.currentTarget;
      const focusableElements = Array.from(
        form.querySelectorAll(
          'input:not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
        )
      );

      const currentIndex = focusableElements.indexOf(e.target);
      if (currentIndex > -1 && currentIndex < focusableElements.length - 1) {
        // Move o foco para o próximo campo
        focusableElements[currentIndex + 1].focus();
      }
    }
  };

  const handleCepBlur = useCallback(
    async (e) => {
      const cep = e.target.value.replace(/\D/g, '');
      if (cep.length !== 8) return;

      setIsFetchingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();

        if (data.erro) {
          toast.warn('CEP não encontrado.');
          return;
        }

        setValue('ENDRESPAC', (data.logradouro || '').toUpperCase(), { shouldDirty: true });
        setValue('BAIRROPAC', (data.bairro || '').toUpperCase(), { shouldDirty: true });
        setValue('CIDADEPAC', (data.localidade || '').toUpperCase(), { shouldDirty: true });
        setValue('ESTADOPAC', (data.uf || '').toUpperCase(), { shouldDirty: true });
        toast.info('Endereço preenchido automaticamente.');
      } catch {
        toast.error('Erro ao consultar CEP.');
      } finally {
        setIsFetchingCep(false);
      }
    },
    [setValue]
  );

  const handleCpfBlur = useCallback(
    async (cpfValue) => {
      const requestId = ++cpfRequestId.current;
      const cleanCpf = String(cpfValue || '').replace(/\D/g, '');

      if (cleanCpf.length !== 11 || isCpfGenericoPaciente(cleanCpf)) {
        setCpfCheck(CPF_CHECK_IDLE);
        return;
      }

      const cpfOriginal = String(paciente?.CPFPAC || '').replace(/\D/g, '');
      if (isEditing && cleanCpf === cpfOriginal) {
        setCpfCheck(CPF_CHECK_IDLE);
        return;
      }

      if (!isValidCPF(cleanCpf)) {
        setCpfCheck({
          status: 'invalid',
          pacientes: [],
          lastCpfChecked: cleanCpf,
          infraError: null,
          message: '',
        });
        return;
      }

      setCpfCheck({
        status: 'loading',
        pacientes: [],
        lastCpfChecked: cleanCpf,
        infraError: null,
        message: '',
      });

      try {
        const encontrados = await getPacientes({ cpf: cleanCpf });
        if (requestId !== cpfRequestId.current) return;

        const codAtual = isEditing ? Number(paciente?.CODPAC) : null;
        const filtrados = Array.isArray(encontrados)
          ? encontrados.filter((p) => !codAtual || Number(p.CODPAC) !== codAtual)
          : [];

        if (filtrados.length > 0) {
          setCpfCheck({
            status: 'duplicate',
            pacientes: filtrados,
            lastCpfChecked: cleanCpf,
            infraError: null,
            message: '',
          });
        } else {
          setCpfCheck(CPF_CHECK_IDLE);
        }
      } catch (error) {
        if (requestId !== cpfRequestId.current) return;

        if (isInfrastructureError(error)) {
          setCpfCheck({
            status: 'infra',
            pacientes: [],
            lastCpfChecked: cleanCpf,
            infraError: error,
            message: '',
          });
        } else {
          setCpfCheck({
            status: 'error',
            pacientes: [],
            lastCpfChecked: cleanCpf,
            infraError: null,
            message:
              error.response?.data?.message ||
              'Não foi possível verificar se este CPF já está cadastrado.',
          });
        }
      }
    },
    [isEditing, paciente]
  );

  const handleCpfChange = useCallback(
    (cpfValue) => {
      const cleanCpf = String(cpfValue || '').replace(/\D/g, '');
      if (cpfCheck.status === 'idle' || cleanCpf === cpfCheck.lastCpfChecked) return;
      cpfRequestId.current += 1;
      setCpfCheck(CPF_CHECK_IDLE);
    },
    [cpfCheck.status, cpfCheck.lastCpfChecked]
  );

  const handleCpfRetry = useCallback(() => {
    if (cpfCheck.lastCpfChecked) {
      handleCpfBlur(cpfCheck.lastCpfChecked);
    }
  }, [cpfCheck.lastCpfChecked, handleCpfBlur]);

  async function handleSalvar(dadosForm, forceCreate = false) {
    setIsSaving(true);
    try {
      const payload = { ...dadosForm, CODFUNC: codFunc, ...(forceCreate && { forceCreate: true }) };
      const salvo = isEditing ? await atualizarPaciente(payload) : await criarPaciente(payload);

      reset(toFormValues(salvo));
      toast.success(
        isEditing
          ? 'Paciente atualizado com sucesso.'
          : `Paciente cadastrado com sucesso. Código: #${salvo?.CODPAC ?? ''}`
      );
      onSucesso?.(salvo);
    } catch (error) {
      // ⚠️ Trata aviso do Backend (Status 409 + HOMONYM_FOUND)
      if (error.response?.status === 409 && error.response?.data?.code === 'HOMONYM_FOUND') {
        setModalData({
          visible: true,
          pacientes: error.response.data.pacientesEncontrados,
          dadosForm: dadosForm, // ✅ Corrigido de 'dados' para 'dadosForm'
        });
        return;
      }

      // ❌ Erros comuns (CPF duplicado, validação, etc)
      toast.error(error.response?.data?.message || 'Erro ao salvar paciente.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <Modal
        show={show}
        onHide={isSaving ? undefined : onHide}
        size="xl"
        centered
        scrollable
        backdrop="static"
        keyboard={!isSaving}
      >
        <Modal.Header
          closeButton={!isSaving}
          className={`border-bottom py-3 ${styles.homePageFonts}`}
        >
          <div className="d-flex align-items-center justify-content-between w-100 pe-2">
            {/* Lado Esquerdo: Título e Identificador */}
            <div className="d-flex align-items-center gap-2">
              <h1 className="fw-bold fs-4 text-primary mb-0">
                {isEditing ? 'Editar Paciente' : 'Novo Paciente'}
              </h1>
              {codPacExibido && (
                <Badge bg="light" text="dark" className="border fs-4 fw-semibold">
                  #{codPacExibido}
                </Badge>
              )}
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="py-3" style={{ background: 'var(--background-color)' }}>
          <Form
            id="form-paciente"
            onSubmit={handleSubmit((dados) => handleSalvar(dados))}
            onKeyDown={handleKeyDownForm}
          >
            <CamposDadosPaciente
              register={register}
              control={control}
              errors={errors}
              nomeFuncLogado={nomeFuncLogado}
              onCepBlur={handleCepBlur}
              isFetchingCep={isFetchingCep}
              onCpfBlur={handleCpfBlur}
              onCpfChange={handleCpfChange}
              onCpfRetry={handleCpfRetry}
              isVerifyingCpf={cpfCheck.status === 'loading'}
              cpfCheck={cpfCheck}
              agendamentosPaciente={agendamentosPaciente}
              isEditing={isEditing}
              watch={watch}
              formSessionKey={formSessionKey}
            />
          </Form>
        </Modal.Body>

        <Modal.Footer className="bg-light border-top d-flex justify-content-between">
          <p className="text-muted small align-items-center">
            <FaLightbulb size={16} className="me-2" />
            Use <kbd>Enter</kbd> para ir ao próximo campo | <kbd>Ctrl</kbd> + <kbd>Enter</kbd> para
            salvar
          </p>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              className="rounded-pill px-4"
              onClick={onHide}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              form="form-paciente"
              variant="success"
              className="rounded-pill px-4 fw-bold"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Salvando...
                </>
              ) : (
                'Salvar Paciente'
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Componente isolado do Modal de Homônimos */}
      <ModalHomonimos
        show={modalData.visible}
        onHide={() => setModalData((prev) => ({ ...prev, visible: false }))}
        pacientes={modalData.pacientes}
        nomeDigitado={modalData.dadosForm?.NOMEPAC}
        onConfirmarCadastro={() => handleSalvar(modalData.dadosForm, true)}
      />
    </>
  );
}
